import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAddCircleOutline,
  MdOutlineDeleteForever,
  MdOutlineFaceRetouchingOff,
  MdOutlineLockReset,
  MdSearch,
} from "react-icons/md";

import { ReactComponent as MenuDots } from "../../../assets/menuDots.svg";
import search_teachers_icon from "../../../assets/searchTeachersIcon.svg";
import user_placeholder from "../../../assets/user-profile.jpg";
import Button from "../../../components/Button";
import Dropdown from "../../../components/Dropdown";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AddTeacherModal from "../../../components/modals/school/AddTeacherModal";
import DeactivateTeacherAccountModal from "../../../components/modals/school/DeactivateTeacherAccountModal";
import DeleteTeacherAccountModal from "../../../components/modals/school/DeleteTeacherAccountModal";
import ResetPasswordModal from "../../../components/modals/school/ResetPasswordModal";
import SuccessResetPasswordModal from "../../../components/modals/school/SuccessResetPasswordModal";
import Pagination from "../../../components/Pagination";
import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import schoolServices from "../../../services/schoolServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const SchoolTeachers = () => {
  const navigate = useNavigateWithLang();
  const { t } = useTranslation();
  const lang = getLanguage();

  const user = useAuth();
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const deactivateModalRef = useRef();
  const addTeacherModalRef = useRef();
  const resetPasswordModalRef = useRef();
  const successResetPasswordModalRef = useRef();
  const deleteTeacherAccountModalRef = useRef();

  const openDeactivateModal = (teacher) => deactivateModalRef.current.openModal(teacher);
  const openAddTeacherModal = () => addTeacherModalRef.current.openModal();
  const openResetPasswordModal = (teacher) => resetPasswordModalRef.current.openModal(teacher);
  const openSuccessResetPasswordModal = (data) => successResetPasswordModalRef.current.openModal(data);
  const openDeleteTeacherAccountModal = (teacher) => deleteTeacherAccountModalRef.current.openModal(teacher);

  const handleDeactivateTeacherAccountSuccess = () => {
    getTeachers();
  };
  const handleResetPassword = (data) => {
    openSuccessResetPasswordModal(data);
  };
  const handleSuccessResetPassword = () => console.log("Password reset");
  const handleDeleteTeacherAccountSuccess = () => {
    setSearchTerm("");
    getTeachers();
  };

  const handleCreateTeacherSuccess = () => {
    getTeachers();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getTeachers = async (page = 1, search = debouncedSearchTerm, order = sortOrder) => {
    setLoading(true);
    try {
      const response = await schoolServices.getTeachersBySchool(user?.school?.id, {
        per_page: 10,
        sort_by: "name",
        sort_dir: order,
        search,
        page,
      });

      setTeachers(response.data);
      setLastPage(response?.last_page);
      setTotalPages(response?.total);
    } catch (error) {
      console.error("Failed to fetch getTeachersBySchool:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateTeacherClassroom = (teacherId) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/classrooms`);
    localStorage.setItem("selectedTeacherId", teacherId);
  };

  const navigateTeacherDetails = (id) => navigate(`/${USER_TYPE.SCHOOL.ROLE}/teachers/${id}`);

  // Debounce effect
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 700ms delay

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  useEffect(() => {
    setLoading(true); // show loading
    setCurrentPage(1); // Reset to page 1 on new search
  }, [debouncedSearchTerm]);

  useEffect(() => {
    getTeachers(currentPage);
  }, [currentPage, debouncedSearchTerm, sortOrder, lang]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-start xl:space-x-2">
        {/* Search */}
        <div className="relative mb-2 flex w-full items-center justify-between rounded-xl bg-[#ffffff] dark:bg-dark-surface xl:mb-0 xl:w-1/3">
          <MdSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-3 rtl:right-auto" />
          <input
            type="text"
            placeholder={t("search")}
            className={`w-full rounded-xl border bg-white px-3 py-2 dark:border-dark-surface dark:bg-[#1F2937] dark:placeholder:text-[#BFBFBF]`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Sort */}
        <Dropdown onSelect={(option) => setSortOrder(option === "A - Z" ? "asc" : "desc")}>
          <div className="cursor-pointer rounded-xl px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#FAFBFC0D]">
            A - Z
          </div>
          <div className="cursor-pointer rounded-xl px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#FAFBFC0D]">
            Z - A
          </div>
        </Dropdown>

        {/* Add Teacher Button */}
        <Button
          color="green"
          iconLeft={<MdAddCircleOutline size={22} />}
          label={t("add_new_teacher")}
          onClick={openAddTeacherModal}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {teachers.map((teacher, index) => {
            return (
              <div
                key={index}
                className={`relative rounded-xl shadow-md ${!teacher?.is_disabled ? "bg-blue-200 dark:bg-[#6590D0]" : "bg-[#dbdbdb] dark:bg-[#626262]"}`}
              >
                <div className="relative px-4 pt-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          !teacher?.is_disabled ? "bg-green-400" : "bg-gray-400"
                        }`}
                      ></span>
                      <span
                        className={`text-xs font-light ${!teacher?.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"}`}
                      >
                        {!teacher?.is_disabled ? t("active") : t("deactivated")}
                      </span>
                    </div>
                    <div className="right-2 top-3">
                      <Menu
                        menuButton={
                          <MenuButton className="flex items-center justify-center">
                            <MenuDots
                              className={`h-3 w-3 ${!teacher.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"}`}
                            />
                          </MenuButton>
                        }
                        transition
                        align="end"
                        menuClassName="select-none rounded-md p-1 text-sm  dark:bg-dark-surface"
                      >
                        <MenuItem
                          onClick={() => openResetPasswordModal(teacher)}
                          className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium"
                        >
                          <MdOutlineLockReset className="text-blue-500" size={20} />
                          <span className="font-medium text-blue-500">{t("reset_password")}</span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => openDeactivateModal(teacher)}
                          className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium"
                        >
                          <MdOutlineFaceRetouchingOff className="text-gray-500" size={19} />
                          <span className="font-medium text-gray-500 dark:text-gray-100">
                            {!teacher?.is_disabled ? t("deactivate_account") : t("activate_account")}
                          </span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => openDeleteTeacherAccountModal(teacher)}
                          className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium"
                        >
                          <MdOutlineDeleteForever size={20} className="text-red-500" />
                          <span className="font-medium text-red-500">{t("delete_account")}</span>
                        </MenuItem>
                      </Menu>
                    </div>
                  </div>
                  {/* Avatar and Info */}
                  <div className="mb-4 mt-4 flex flex-col items-center">
                    <img
                      className="mb-3 h-28 w-28 rounded-full border-4 border-white object-cover"
                      src={teacher.image || user_placeholder}
                      alt={teacher.name}
                    />

                    <TruncatedTextWithTooltip
                      text={teacher.name}
                      maxLength={20}
                      className={`inline-block text-center font-semibold capitalize ${
                        !teacher?.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"
                      }`}
                    />
                  </div>
                  <div className="mb-4">
                    <p className="flex justify-between">
                      <button
                        className="text-sm text-blue-500 underline"
                        disabled={teacher.is_disabled}
                        onClick={() => navigateTeacherClassroom(teacher.id)}
                      >
                        {t("classrooms")}
                      </button>

                      <span
                        className={`text-sm font-medium ${!teacher?.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"}`}
                      >
                        {teacher.classrooms_count}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span
                        className={`text-sm font-medium ${!teacher?.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"}`}
                      >
                        {t("students")}
                      </span>
                      <span
                        className={`text-sm font-medium ${!teacher?.is_disabled ? "text-text dark:text-white" : "text-text dark:text-white"}`}
                      >
                        {teacher.students_count}
                      </span>
                    </p>
                  </div>
                </div>
                {/* View Details Button */}
                <div className="flex w-full">
                  <Button
                    label={t("view_details")}
                    disabled={teacher.is_disabled}
                    onClick={() => navigateTeacherDetails(teacher.id)}
                    className={`w-full rounded-xl rounded-tl-none rounded-tr-none px-4 py-2 transition duration-300 ${
                      !teacher?.is_disabled
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "bg-gray-300 text-gray-50"
                    }`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Pagination */}
      {totalPages > 9 && (
        <Pagination lastPage={lastPage} currentPage={currentPage} onPageChange={handlePageChange} />
      )}

      {/* Modals */}
      <AddTeacherModal ref={addTeacherModalRef} onSubmit={handleCreateTeacherSuccess} />
      <DeactivateTeacherAccountModal
        ref={deactivateModalRef}
        onSubmit={handleDeactivateTeacherAccountSuccess}
      />
      <ResetPasswordModal ref={resetPasswordModalRef} onSubmit={handleResetPassword} />
      <SuccessResetPasswordModal ref={successResetPasswordModalRef} onSubmit={handleSuccessResetPassword} />
      <DeleteTeacherAccountModal
        ref={deleteTeacherAccountModalRef}
        onSubmit={handleDeleteTeacherAccountSuccess}
      />
    </div>
  );
};

export default SchoolTeachers;
